import { GroupSelectorDropdown, PageHeader } from "@idwal/idwal-react-components"
import { useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { pathMapper } from "../../utils/pathMapper"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { getUserOrganisationsInfoThunk, setSelectedGroupName, userSliceSelector } from "../../features/userSlice"
import { setOrganizationId } from "../../features/instantQuotationSlice";
import { useEffect } from "react"
import { inspectionsSelector } from "../../features/inspectionsSlice"
import { ERROR_ROUTE, INSPECTION_ROUTE, INSPECTIONS_ROUTE, ORDERS_ROUTE, REPORTS_ROUTE, SCREENING_ROUTE } from "../../config/router"
import { QUOTATIONS_ROUTE } from "../../config/router"
import { quotationsSelector } from "../../features/quotationsSlice"
import { ORGANISATION_DROP_DOWN_KEY } from "../../utils/Constants"

export const PageHeaderWrapper = () => {
    const { t } = useTranslation();

    const {
        SelectedGroupName,
        UserServiceInfo,
        UserInfoStatus,
        OrganisationsInfo,
        OrganisationsInfoStatus
    } = useAppSelector(userSliceSelector);

    const {
        selectedInspection,
        InspectionsStatus
    } = useAppSelector(inspectionsSelector);
    
    const { 
        SelectedQuotation, 
        SelectedQuotationStatus 
    } = useAppSelector(quotationsSelector);
    const dispatch = useAppDispatch();

    const { pathname } = useLocation();
    let currentPage = pathname.split("/").pop() ?? "";
    currentPage = currentPage === "" ? t("pageHeader.homePage") : currentPage;
    let pathCrumbs = [...pathname.split("/").slice(1)];

    const isOnInspectionPage = pathCrumbs.find((path) => path.toLowerCase() === INSPECTION_ROUTE.toLowerCase()) && pathCrumbs.length > 1;

    const isOnErrorPage = pathCrumbs.find((path) => path.toLowerCase() === ERROR_ROUTE.toLowerCase())

    if (isOnInspectionPage && pathCrumbs.length > 1) {
        currentPage = selectedInspection ? selectedInspection : ''
    }

    let mappedCurrentPage = pathMapper(currentPage, t) ?? "";
    let mappedPaths = pathCrumbs.map((path) => pathMapper(path, t));

    const setSelectedOrg = (userOrgs: string[]) => {
        let sessionStorageOrg = sessionStorage.getItem(ORGANISATION_DROP_DOWN_KEY);
        if (sessionStorageOrg && userOrgs.includes(sessionStorageOrg)) {
            dispatch(setOrganizationId(sessionStorageOrg));
            dispatch(setSelectedGroupName(sessionStorageOrg));
            return;
        }

        if (UserServiceInfo?.organizations != null && UserServiceInfo?.organizations.length > 1) {
            sessionStorage.setItem(ORGANISATION_DROP_DOWN_KEY, userOrgs[userOrgs.length - 1]);
            dispatch(setOrganizationId(userOrgs[userOrgs.length - 1]));
            setSelectedGroupName(userOrgs[userOrgs.length - 1]);
        } else {
            sessionStorage.setItem(ORGANISATION_DROP_DOWN_KEY, userOrgs[0]);
            dispatch(setOrganizationId(userOrgs[0]));
            setSelectedGroupName(userOrgs[0]);
        }
    }

    useEffect(() => {
        if (UserInfoStatus === "succeeded" && OrganisationsInfoStatus === "idle") {
            setSelectedOrg(UserServiceInfo?.organizations ?? []);
            dispatch(getUserOrganisationsInfoThunk(UserServiceInfo?.organizations ?? []));
        }
    }, [UserServiceInfo, UserInfoStatus, OrganisationsInfo, OrganisationsInfoStatus]);

    const handleOrgChange = (e: any) => {
        sessionStorage.setItem(ORGANISATION_DROP_DOWN_KEY, e.value.code);
        dispatch(setOrganizationId(e.value.code));
        dispatch(setSelectedGroupName(e.value.code));
    }

    const orgDropdown = () => {
        if (OrganisationsInfoStatus !== "succeeded") {
            return <></>;
        }

        if (UserServiceInfo?.organizations.length === 1) {
            handleOrgChange({ value: { code: UserServiceInfo?.organizations[0] } });
            return <></>;
        }

        const mappedOrgs = OrganisationsInfo.map((org) => {
            return {
                name: org.name.toString(),
                code: org.id.toString()
            }
        }) ?? [];

        if (mappedOrgs.length === 0) {
            return <></>;
        }

        return (
            <GroupSelectorDropdown 
                selectedGroup={SelectedGroupName !== "" ? SelectedGroupName : mappedOrgs[0].code} 
                onGroupChange={handleOrgChange} 
                groups={mappedOrgs}
            />
        );
    }

    if (isOnInspectionPage && InspectionsStatus === 'pending') {
        return <></>
    }

    const isOnQuotationsRoute = pathCrumbs.find((path) => path.toLowerCase() === QUOTATIONS_ROUTE.toLowerCase()) && pathCrumbs.length > 1;

    if (isOnQuotationsRoute) {
        mappedPaths = SelectedQuotation ? [t("pageHeader.quotations")] : [];
        mappedCurrentPage = SelectedQuotation?.quotationNumber ?? '';
    }

    const isOnScreeningRoute = pathCrumbs.find((path) => path.toLowerCase() === SCREENING_ROUTE.toLowerCase()) && pathCrumbs.length > 1;

    if (isOnScreeningRoute) {
        mappedPaths = [t("pageHeader.screening")];
        mappedCurrentPage = pathCrumbs[1];
    }

    if (isOnInspectionPage) {
        mappedPaths = [t("pageHeader.reports")];
    }

    if (mappedPaths.length === 1 && !isOnQuotationsRoute && !isOnInspectionPage && !isOnScreeningRoute) {
        mappedPaths = [];
    }

    if (isOnErrorPage) {
        mappedPaths = [t("pageHeader.error")];
    }

    const implementedRoutes = [
        INSPECTIONS_ROUTE,
        REPORTS_ROUTE,
        INSPECTION_ROUTE,
        QUOTATIONS_ROUTE,
        ORDERS_ROUTE,
        SCREENING_ROUTE,
        ERROR_ROUTE,
    ];

    if (
        !implementedRoutes.includes(mappedCurrentPage.toLowerCase()) && 
        mappedPaths.length == 0 ||
        (
            mappedPaths[0] && 
            mappedPaths[0].toLowerCase() === 'error'
        ) 
    ) {
        mappedPaths = [];
        mappedCurrentPage = "";
    }

    return (
        <PageHeader 
            currentPageCrumb={mappedCurrentPage || ""}
            pathCrumbs={mappedPaths}
            rhsInteractiveElements={[orgDropdown()]}        
        />
    )
}
